import React, { useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, StatCard, StatusBadge } from "@/components/common";
import { SelectField, DateField } from "@/components/fields";
import { money } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartBar, CurrencyDollar, HandCoins, FunnelSimple, X, DownloadSimple, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

const REPORTS = [
  { key: "properties", label: "All Properties" },
  { key: "vacant", label: "Vacant Properties" },
  { key: "occupied", label: "Occupied Properties" },
  { key: "arrears", label: "Properties in Arrears" },
  { key: "no_arrears", label: "Without Arrears" },
  { key: "rental_collection", label: "Rental Collection" },
  { key: "management_fees", label: "Management Fees" },
  { key: "commissions", label: "Commissions" },
  { key: "staff_performance", label: "Staff Performance" },
  { key: "revenue", label: "Revenue" },
  { key: "expenses", label: "Expenses" },
  { key: "profit_loss", label: "Profit & Loss" },
  { key: "deposits", label: "Property Deposits" },
];

export default function Reports() {
  const { company } = useAuth();
  const cur = company?.currency || "KES";
  const [active, setActive] = useState("properties");
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [props, setProps] = useState([]);
  const [f, setF] = useState({ date_from: "", date_to: "", staff_id: "", landlord_id: "", property_id: "" });

  useEffect(() => {
    api.get("/users").then((r) => setStaff(r.data.filter((u) => u.role === "staff"))).catch(() => {});
    api.get("/landlords").then((r) => setLandlords(r.data)).catch(() => {});
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let ignore = false;
    setData(null);
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    api.get(`/reports/${active}?${p.toString()}`)
      .then((r) => { if (!ignore) setData(r.data); })
      .catch(() => { if (!ignore) setData([]); });
    return () => { ignore = true; };
  }, [active, f]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const clear = () => setF({ date_from: "", date_to: "", staff_id: "", landlord_id: "", property_id: "" });
  const hasFilters = Object.values(f).some(Boolean);

  const queryString = () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  };

  const exportReport = async (format) => {
    try {
      await downloadFile(`/reports/${active}/export?format=${format}&${queryString()}`, `${active}-report.${format}`);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const render = () => {
    if (data === null) return <Loader />;
    if (active === "rental_collection")
      return <div className="grid grid-cols-2 gap-4"><StatCard label="Total Collected" value={money(data.total_collected, cur)} icon={CurrencyDollar} /><StatCard label="Payments" value={data.count} icon={ChartBar} accent="sky" /></div>;
    if (active === "management_fees")
      return <div className="grid grid-cols-2 gap-4"><StatCard label="Total Fees" value={money(data.total, cur)} icon={CurrencyDollar} /><StatCard label="Count" value={data.count} icon={ChartBar} accent="sky" /></div>;
    if (active === "commissions")
      return <StatCard label="Total Commissions" value={money(data.total, cur)} icon={HandCoins} />;
    if (active === "revenue")
      return <StatCard label="Management Fee Revenue" value={money(data.management_fee_revenue, cur)} icon={CurrencyDollar} />;
    if (active === "profit_loss")
      return <div className="grid grid-cols-3 gap-4"><StatCard label="Income" value={money(data.income, cur)} icon={CurrencyDollar} /><StatCard label="Expenses" value={money(data.expenses, cur)} accent="terracotta" icon={CurrencyDollar} /><StatCard label="Profit" value={money(data.profit, cur)} accent="sky" icon={ChartBar} /></div>;
    if (active === "deposits")
      return (<div><div className="grid grid-cols-3 gap-4 mb-4"><StatCard label="Total Due" value={money(data.total_due, cur)} icon={CurrencyDollar} /><StatCard label="Total Paid" value={money(data.total_paid, cur)} accent="sky" icon={CurrencyDollar} /><StatCard label="Balance" value={money(data.balance, cur)} accent="terracotta" icon={ChartBar} /></div>
        <Card className="overflow-hidden"><Table><TableHeader><TableRow><TableHead>Property</TableHead><TableHead>Tenant</TableHead><TableHead className="text-right">Due</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>{(data.deposits || []).map((d, i) => <TableRow key={i} data-testid={`deposit-row-${i}`}><TableCell className="font-medium">{d.property_name} {d.unit_no}</TableCell><TableCell>{d.tenant_name}</TableCell><TableCell className="text-right font-mono">{money(d.deposit_due, cur)}</TableCell><TableCell className="text-right font-mono">{money(d.deposit_paid, cur)}</TableCell><TableCell className="text-right font-mono">{money(d.balance, cur)}</TableCell><TableCell><StatusBadge status={d.status} /></TableCell></TableRow>)}</TableBody></Table></Card></div>);
    if (active === "expenses")
      return (<Card className="p-5"><h3 className="font-semibold mb-3">By Category — Total {money(data.total, cur)}</h3>
        {Object.entries(data.by_category || {}).map(([k, v]) => <div key={k} className="flex justify-between py-1.5 border-b text-sm"><span className="capitalize">{k.replace(/_/g, " ")}</span><span className="font-mono">{money(v, cur)}</span></div>)}</Card>);
    if (active === "staff_performance")
      return (<Card className="overflow-hidden"><Table><TableHeader><TableRow><TableHead>Staff</TableHead><TableHead className="text-right">Properties</TableHead><TableHead className="text-right">Tenants</TableHead><TableHead className="text-right">Commission</TableHead></TableRow></TableHeader>
        <TableBody>{(Array.isArray(data) ? data : []).map((s) => <TableRow key={s.id}><TableCell>{s.name}</TableCell><TableCell className="text-right font-mono">{s.properties_sourced}</TableCell><TableCell className="text-right font-mono">{s.tenants_sourced}</TableCell><TableCell className="text-right font-mono">{money(s.total_commission, cur)}</TableCell></TableRow>)}</TableBody></Table></Card>);
    if (["arrears", "no_arrears"].includes(active))
      return (<Card className="overflow-hidden"><Table><TableHeader><TableRow><TableHead>Tenant</TableHead><TableHead>Property</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
        <TableBody>{(Array.isArray(data) ? data : []).map((r, i) => <TableRow key={`${r.tenant_name}-${r.property_name}-${i}`}><TableCell>{r.tenant_name}</TableCell><TableCell className="text-sm">{r.property_name} {r.unit_no}</TableCell><TableCell className="text-right font-mono">{money(r.balance, cur)}</TableCell></TableRow>)}</TableBody></Table></Card>);
    return (<Card className="overflow-hidden"><Table><TableHeader><TableRow><TableHead>Property</TableHead><TableHead>Type</TableHead><TableHead>Location</TableHead><TableHead>Status</TableHead>{active === "vacant" && <TableHead className="text-right">Vacant Days</TableHead>}</TableRow></TableHeader>
      <TableBody>{(Array.isArray(data) ? data : []).map((p) => <TableRow key={p.id}><TableCell className="font-medium">{p.name} {p.unit_no}</TableCell><TableCell>{p.property_type}</TableCell><TableCell className="text-sm">{[p.estate, p.city].filter(Boolean).join(", ")}</TableCell><TableCell>{p.status}</TableCell>{active === "vacant" && <TableCell className="text-right font-mono">{p.vacancy_days ?? "—"}</TableCell>}</TableRow>)}</TableBody></Table></Card>);
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Operational & financial reporting" icon={ChartBar}
        actions={<>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => exportReport("csv")} data-testid="export-csv"><DownloadSimple size={16} /> CSV</Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => exportReport("pdf")} data-testid="export-pdf"><FilePdf size={16} /> PDF</Button>
        </>} />
      <div className="flex flex-wrap gap-2 mb-4">
        {REPORTS.map((r) => (
          <button key={r.key} onClick={() => setActive(r.key)} data-testid={`report-${r.key}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors border ${active === r.key ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-secondary"}`}>
            {r.label}
          </button>
        ))}
      </div>

      <Card className="p-4 mb-6">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-primary"><FunnelSimple size={16} weight="duotone" /> Filters</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <DateField label="From" value={f.date_from} onChange={(v) => set("date_from", v)} testId="filter-from" />
          <DateField label="To" value={f.date_to} onChange={(v) => set("date_to", v)} testId="filter-to" />
          <SelectField label="Staff" value={f.staff_id} onChange={(v) => set("staff_id", v)} testId="filter-staff"
            options={[{ value: "", label: "All Staff" }, ...staff.map((s) => ({ value: s.id, label: s.name }))]} />
          <SelectField label="Landlord" value={f.landlord_id} onChange={(v) => set("landlord_id", v)} testId="filter-landlord"
            options={[{ value: "", label: "All Landlords" }, ...landlords.map((l) => ({ value: l.id, label: l.name }))]} />
          <SelectField label="Property" value={f.property_id} onChange={(v) => set("property_id", v)} testId="filter-property"
            options={[{ value: "", label: "All Properties" }, ...props.map((p) => ({ value: p.id, label: `${p.name} ${p.unit_no || ""}` }))]} />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" className="mt-3 gap-1 text-muted-foreground" onClick={clear} data-testid="clear-filters">
            <X size={14} /> Clear filters
          </Button>
        )}
      </Card>

      {render()}
    </div>
  );
}
